import { FilterOperation } from '../filter/filter-criteria';
import { assertBackendFieldNamesAreUnique } from './backend-field-names';
import { IrdEntitySchema, IrdEntitySchemaImpl } from './metadata';
import { FilterOption, IrdParser, IrdProperty, IrdPropertyImpl, IrdPropertyType } from './property';
import { IrdValidator } from './validator';

/**
 * @see https://dzone.com/articles/nested-builder
 */
export class IrdEntitySchemaBuilder<T> {
  private readonly instance: IrdEntitySchema<T>;
  private readonly daoNames = new Map<string, string>();

  constructor(primaryKey: keyof T, translationKeyPrefix: string) {
    this.instance = new IrdEntitySchemaImpl(primaryKey, translationKeyPrefix);
  }

  /**
   * Adopts the backend field names already declared by another view of the same entity, so a
   * rename is stated once and read here rather than restated.
   *
   * Matching is by property name, and a miss is silent: a view that calls the field something
   * else adopts nothing and resolves against its own name. Where the two views disagree on the
   * name — a formatted row field beside the DTO's raw one, say — read the declared name out of
   * the schema explicitly rather than letting the fallback happen to be right.
   *
   * Call this before {@link addProperties} — properties built earlier have already taken their
   * names.
   */
  public daoNamesFrom<S>(schema: IrdEntitySchema<S>): this {
    for (const property of Object.values(schema.properties) as IrdProperty<S, IrdPropertyType>[]) {
      if (property.daoName) {
        this.daoNames.set(property.name.toString(), property.daoName);
      }
    }
    return this;
  }

  public withEditDialog(): this {
    this.instance.withEditDialog = true;
    return this;
  }

  public withProperties(properties: Record<keyof T, IrdProperty<T, IrdPropertyType>>): this {
    this.instance.properties = properties;
    return this;
  }

  public addProperties() {
    return new PropertySetBuilder(this.instance.translationKeyPrefix, this, this.daoNames);
  }

  public build(): IrdEntitySchema<T> {
    assertBackendFieldNamesAreUnique(
      (Object.values(this.instance.properties) as IrdProperty<T, IrdPropertyType>[]).map((property) => ({
        name: property.nameAsString(),
        daoName: property.daoName,
      })),
    );
    return this.instance;
  }
}

class PropertySetBuilder<T> {
  private instance: Record<keyof T, IrdProperty<T, IrdPropertyType>> = {} as Record<keyof T, IrdProperty<T, IrdPropertyType>>; //TODO type conversion;

  constructor(
    private translationKeyPrefix: string,
    private readonly metadataBuilder: IrdEntitySchemaBuilder<T>,
    private readonly daoNames = new Map<string, string>(),
  ) {}

  public withProperty<O>(property: IrdProperty<T, O>): this {
    // A property handed in already built skips PropertyBuilder, so adopt the backend name it would
    // have. A no-op for a property that came from addProperty().
    property.daoName ??= this.daoNames.get(property.name.toString());
    this.instance[property.name] = property;
    return this;
  }

  public addProperty<O>(name: keyof T, type: IrdPropertyType): PropertyBuilder<T, O> {
    return new PropertyBuilder(name, type, this.translationKeyPrefix, this, this.daoNames.get(name.toString()));
  }

  private build(): Record<keyof T, IrdProperty<T, IrdPropertyType>> {
    return this.instance;
  }

  public done(): IrdEntitySchemaBuilder<T> {
    this.metadataBuilder.withProperties(this.build());
    return this.metadataBuilder;
  }
}

class PropertyBuilder<T, O> {
  private instance: IrdProperty<T, O>;

  constructor(
    name: keyof T,
    type: IrdPropertyType,
    translationKeyPrefix: string,
    private propertySetBuilder: PropertySetBuilder<T>,
    daoName?: string,
  ) {
    this.instance = new IrdPropertyImpl(name, type, translationKeyPrefix);
    this.instance.daoName = daoName;
  }

  /**
   * Declares the name the backend knows this field by, where it differs from the property name.
   * The table's sort resolution and the object mappers both read it, so it is stated only here.
   */
  public daoName(name: string): this {
    this.instance.daoName = name;
    return this;
  }

  public addValidator(validator: IrdValidator): this {
    this.instance.validator.push(validator);
    return this;
  }

  public addAsyncValidator(asyncValidator: IrdValidator): this {
    this.instance.asyncValidator.push(asyncValidator);
    return this;
  }

  public parser(parser: IrdParser): this {
    this.instance.parser = parser;
    return this;
  }

  public hideInUi(): this {
    this.instance.showInUi = false;
    return this;
  }

  public showInUi(): this {
    this.instance.showInUi = true;
    return this;
  }

  public isArray(): this {
    this.instance.array = true;
    return this;
  }

  /**
   * The field is shown but can be changed neither in the edit dialog nor inline — unless a form or
   * table declaration sets `editable: true` for it.
   */
  public readOnly(): this {
    this.instance.editable = false;
    return this;
  }

  public filterable(config?: { operations?: FilterOperation[]; values?: FilterOption[]; jsonKeys?: FilterOption[] }): this {
    this.instance.filterable = true;
    if (config?.operations) {
      this.instance.filterOperations = config.operations;
    }
    if (config?.values) {
      this.instance.allowedValues = config.values;
    }
    if (config?.jsonKeys) {
      this.instance.filterJsonKeys = config.jsonKeys;
    }
    return this;
  }

  public isObject(): this {
    this.instance.array = false;
    return this;
  }

  private build(): IrdProperty<T, O> {
    return this.instance;
  }

  public done(): PropertySetBuilder<T> {
    this.propertySetBuilder.withProperty(this.build());
    return this.propertySetBuilder;
  }
}
